/**
 * Loading the catalog out of IndexedDB.
 *
 * The catalog reads local rows and never the server. That is not an
 * optimisation: the phone is at a table, possibly in a cellar, and "what did we
 * think of this" has to answer offline or it does not answer. The sync layer
 * fills the store in the background; this hook just watches it.
 *
 * Reloads are driven by the two signals the store already emits — the outbox
 * notifies on every local write, and the sync state changes when a pull lands
 * rows. Polling would be the wrong shape for a screen that is usually static
 * for minutes at a time.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { localiseSnapshot, useLocale } from "../../locale.ts";
import { getAll, idbAvailable } from "../../store/idb.ts";
import { subscribeOutbox } from "../../store/outbox.ts";
import { subscribeSync } from "../../store/sync.ts";
import { EMPTY_LIBRARY, type Library } from "./data.ts";
import type {
  AspectRating,
  Game,
  HeadlineRating,
  OverallRating,
  PlayAgain,
  PlaySession,
  Profile,
  SessionAspect,
  SessionParticipant,
  SessionSubQuestion,
  Stop,
  SubQuestionRating,
} from "../../../shared/types.ts";

/**
 * Stops are stored as JSON text when they arrive from the server and as an
 * array when written locally. Normalising on read keeps every consumer from
 * having to know which path a row came down.
 */
function parseStops(raw: unknown): Stop[] {
  if (Array.isArray(raw)) return raw as Stop[];
  if (typeof raw !== "string") return [];
  try {
    const parsed: unknown = JSON.parse(raw);
    return Array.isArray(parsed) ? (parsed as Stop[]) : [];
  } catch {
    return [];
  }
}

async function load(): Promise<Library> {
  if (!idbAvailable()) return EMPTY_LIBRARY;

  const [
    games,
    sessions,
    participants,
    headlines,
    sessionAspects,
    aspectRatings,
    sessionSubQuestions,
    subquestionRatings,
    overallRatings,
    playAgain,
    profiles,
  ] = await Promise.all([
    getAll<Game>("game"),
    getAll<PlaySession>("play_session"),
    getAll<SessionParticipant>("session_participant"),
    getAll<HeadlineRating>("headline_rating"),
    getAll<SessionAspect>("session_aspect"),
    getAll<AspectRating>("aspect_rating"),
    getAll<SessionSubQuestion>("session_subquestion"),
    getAll<SubQuestionRating>("subquestion_rating"),
    getAll<OverallRating>("overall_rating"),
    getAll<PlayAgain>("play_again"),
    getAll<Profile>("profile"),
  ]);

  return {
    games,
    sessions,
    participants,
    headlines,
    sessionAspects: sessionAspects.map((sa) => ({ ...sa, stops_snapshot: parseStops(sa.stops_snapshot) })),
    aspectRatings,
    // Stops are parsed for the same reason the aspect's are — a follow-up that
    // came down from the server carries them as JSON text.
    sessionSubQuestions: sessionSubQuestions.map((sq) => ({
      ...sq,
      stops_snapshot: parseStops(sq.stops_snapshot),
    })),
    subquestionRatings,
    overallRatings,
    playAgain,
    profiles,
  };
}

export type LibraryStatus = "loading" | "ready" | "error";

export interface LibraryHandle {
  library: Library;
  status: LibraryStatus;
  error: string | null;
  reload: () => void;
}

export function useLibrary(): LibraryHandle {
  const [library, setLibrary] = useState<Library>(EMPTY_LIBRARY);
  const [status, setStatus] = useState<LibraryStatus>("loading");
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);
  const alive = useRef(true);

  const reload = useCallback(() => setTick((n) => n + 1), []);

  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const next = await load();
        if (!cancelled && alive.current) {
          setLibrary(next);
          setStatus("ready");
          setError(null);
        }
      } catch (err) {
        if (cancelled || !alive.current) return;
        setStatus("error");
        setError(err instanceof Error ? err.message : String(err));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [tick]);

  useEffect(() => {
    const offOutbox = subscribeOutbox(reload);
    // `subscribeSync` fires immediately with the current state; the extra
    // reload that causes is cheap and keeps the first paint honest.
    const offSync = subscribeSync(() => reload());
    return () => {
      offOutbox();
      offSync();
    };
  }, [reload]);

  // Same split as `useSessionLive`: the loaded rows stay as they are on disk and
  // the language is a lens over them, so changing it repaints the shelf without
  // a reload and without rewriting a single stored snapshot.
  const locale = useLocale();
  const shown = useMemo<Library>(
    () => ({
      ...library,
      sessionAspects: library.sessionAspects.map((sa) => localiseSnapshot(sa, locale)),
    }),
    [library, locale],
  );

  return { library: shown, status, error, reload };
}
